import { useState } from "react";
import {
  Button,
  ConfigProvider,
  Empty,
  Flex,
  Input,
  InputNumber,
  Table,
} from "antd";
import TagBordered from "../../../../../../../../../../atom/TagBordered";

import StyledContainer from "./style";

import { MODEL_INVESTMENT_AMOUNT } from "../../../../constant";

import {
  cleanPercentValue,
  getCurrencyValue,
  getPercentValue,
  selectValue,
} from "../../../../../../../../../../utils/helper/general";

import { ReactComponent as IconColumns } from "../../image/icon_columns.svg";
import { ReactComponent as IconDollars } from "../../image/icon_dollars.svg";
import { ReactComponent as IconPlusCircled } from "../../image/icon_plus_circled.svg";
import { ReactComponent as IconRemoveCircled } from "../../image/icon_remove_circled.svg";
import { SearchOutlined } from "@ant-design/icons";

const TableHoldings = ({
  columnPreferences,
  hideButtonColumns,
  isTemplate,
  onClickAddHoldings,
  onClickColumns,
  onClickRemoveHolding,
  onClickReWeightPortfolio,
  onToggleWeightType,
  selectedHoldings,
  updateHoldingWeight,
  weightType,
}) => {
  const [searchedText, setSearchedText] = useState("");

  const isPercentageWeightType = weightType === "percentage";
  const selectedHoldingsTotal = selectedHoldings.reduce(
    (acc, it) => acc + it.weight,
    0
  );

  const columns = [
    {
      filteredValue: [searchedText],
      key: "name",
      onFilter: (value, record) =>
        record.name.toLowerCase().includes(value?.toLowerCase()),
      render: allValues => allValues.name,
      title: <b>Model Holdings</b>,
    },
    {
      key: "ticker",
      render: allValues => allValues.ticker ?? "n/a",
      title: <b>Ticker</b>,
      width: 59,
    },
    {
      align: "right",
      key: "return",
      render: allValues =>
        getPercentValue(
          allValues.properties?.mcVolatilityReturn ??
            Math.round(allValues.RiskNumber * 1000) / 10
        ),
      title: <b>Return</b>,
      width: 59,
    },
    {
      key: "category",
      render: allValues => <TagBordered label={allValues.broadCategory} />,
      title: <b>Category</b>,
      width: 115,
    },
    {
      align: "right",
      key: "weight",
      render: allValues => (
        <Flex align="center" gap={16} justify="end">
          {isPercentageWeightType ? (
            <InputNumber
              controls={false}
              disabled={isTemplate}
              max={100}
              min={0}
              onChange={value =>
                updateHoldingWeight({
                  id: allValues._id,
                  value,
                })
              }
              formatter={value =>
                getPercentValue(Math.round(value * 10000) / 100)
              }
              parser={value => cleanPercentValue(value) / 100}
              onClick={selectValue}
              value={allValues.weight}
            />
          ) : (
            <InputNumber
              controls={false}
              disabled={isTemplate}
              max={MODEL_INVESTMENT_AMOUNT}
              min={0}
              onChange={value =>
                updateHoldingWeight({
                  id: allValues._id,
                  value: Math.round(value),
                })
              }
              onClick={selectValue}
              value={allValues.weight}
            />
          )}

          <Button
            disabled={isTemplate}
            icon={<IconRemoveCircled />}
            onClick={() =>
              onClickRemoveHolding({ activeHoldingId: allValues._id })
            }
            type="text"
          />
        </Flex>
      ),
      title: (
        <Flex gap={16} justify="end">
          <Button
            onClick={onToggleWeightType}
            icon={<IconDollars />}
            shape="round"
            size="small"
          />

          {!hideButtonColumns && (
            <Button
              disabled={isTemplate}
              icon={<IconColumns />}
              onClick={onClickColumns}
              shape="round"
              size="small"
            >
              Columns
            </Button>
          )}
        </Flex>
      ),
      width: 163,
    },
  ];

  const sortedColumns = columnPreferences?.length
    ? columnPreferences
        .filter(pref => pref.visible) // only include visible columns
        .map(pref => columns.find(col => col.key === pref.type))
        .filter(Boolean) // remove any undefined if mismatch
    : columns;

  return (
    <StyledContainer vertical>
      <Flex justify="space-between" style={{ margin: "24px 0" }}>
        <Input
          onChange={event => setSearchedText(event.target.value)}
          placeholder="Search..."
          style={{
            maxWidth: 249,
            height: 36,
            borderRadius: 25,
          }}
          suffix={<SearchOutlined />}
        />

        <Button
          disabled={isTemplate}
          icon={<IconPlusCircled />}
          onClick={onClickAddHoldings}
          shape="round"
        >
          Holdings
        </Button>
      </Flex>

      <ConfigProvider
        theme={{
          components: {
            Table: {
              borderColor: "#C2C2C2",
              headerBg: "#fff",
              headerSplitColor: "transparent",
              headerColor: "#052C2B",
            },
          },
        }}
        renderEmpty={() =>
          selectedHoldings?.length === 0 && (
            <Empty
              image={false}
              description={
                <Button
                  icon={<IconPlusCircled />}
                  onClick={onClickAddHoldings}
                  shape="round"
                  type="primary"
                >
                  Add Holdings
                </Button>
              }
            />
          )
        }
      >
        <Table
          columns={sortedColumns}
          dataSource={selectedHoldings}
          pagination={false}
          rowHoverable={false}
          size="small"
          summary={() =>
            selectedHoldings?.length !== 0 && (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={sortedColumns.length} index={1}>
                  <Flex
                    align="center"
                    gap={16}
                    justify="end"
                    style={{ paddingRight: 16 }}
                  >
                    <Button
                      onClick={onClickReWeightPortfolio}
                      shape="round"
                      size="small"
                    >
                      Re-weight Portfolio
                    </Button>

                    <span>Total</span>
                    <b>
                      {isPercentageWeightType
                        ? getPercentValue(
                            Math.round(selectedHoldingsTotal * 100)
                          )
                        : getCurrencyValue(selectedHoldingsTotal)}
                    </b>
                  </Flex>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )
          }
        />
      </ConfigProvider>
    </StyledContainer>
  );
};

export default TableHoldings;
